<template>
  <div class="weekly-entry">
    <div v-if="loadingProjects" class="wk-card text-center">
      <b-spinner small /> Loading…
    </div>

    <b-alert v-else-if="projectsError" show variant="danger">
      {{ projectsError }}
      <a href="#" class="alert-link ml-1" @click.prevent="loadProjects">
        Try again
      </a>
    </b-alert>

    <div v-else-if="!projects.length" class="wk-card" data-test="no-projects">
      Weekly timesheets aren't turned on for any of your projects. Ask your
      company admin if you think they should be.
    </div>

    <template v-else>
      <!-- Project and week -->
      <section class="wk-card wk-pickers">
        <b-form-group label="Project" class="mb-0">
          <b-form-select
            :value="projectId"
            :options="projectOptions"
            data-test="project"
            @change="onProjectChange"
          />
        </b-form-group>
        <b-form-group label="Week" class="mb-0">
          <div class="wk-week">
            <b-button
              variant="outline-secondary"
              :disabled="!projectId || loadingSheet"
              aria-label="Previous week"
              data-test="prev-week"
              @click="shiftWeek(-1)"
            >
              ‹
            </b-button>
            <b-form-input
              :value="weekStart"
              type="date"
              :disabled="!projectId || loadingSheet"
              data-test="week"
              @change="onWeekPicked"
            />
            <b-button
              variant="outline-secondary"
              :disabled="!projectId || loadingSheet"
              aria-label="Next week"
              data-test="next-week"
              @click="shiftWeek(1)"
            >
              ›
            </b-button>
          </div>
          <small class="text-muted">{{ weekLabel }}</small>
        </b-form-group>
      </section>

      <div v-if="loadingSheet" class="wk-card text-center">
        <b-spinner small /> Loading your week…
      </div>

      <b-alert v-else-if="sheetError" show variant="danger">
        {{ sheetError }}
        <a href="#" class="alert-link ml-1" @click.prevent="openWeek">
          Try again
        </a>
      </b-alert>

      <template v-else-if="sheet">
        <b-alert :show="!editable" variant="secondary" data-test="locked">
          {{ statusLabel }}. This week can no longer be changed.
        </b-alert>
        <b-alert :show="cancelsLink" variant="warning" data-test="pending">
          This week is awaiting approval. Changing it takes it back to draft and
          cancels the link sent to your supervisor.
        </b-alert>
        <b-alert
          :show="sheet.status === 'rejected'"
          variant="danger"
          data-test="rejected"
        >
          Rejected<template v-if="rejectionNote">: {{ rejectionNote }}</template
          >. Fix your hours and send it again.
        </b-alert>

        <!-- Standard Hours -->
        <section v-if="editable" class="wk-card" data-test="standard-hours">
          <h2 class="wk-h2">Standard Hours</h2>
          <p class="text-muted small">
            One start, finish and break for the days you tick.
          </p>
          <div class="wk-std">
            <b-form-group label="Start" class="mb-0">
              <b-form-input
                v-model="std.start"
                type="time"
                data-test="std-start"
              />
            </b-form-group>
            <b-form-group label="Finish" class="mb-0">
              <b-form-input
                v-model="std.finish"
                type="time"
                data-test="std-finish"
              />
            </b-form-group>
            <b-form-group label="Break (min)" class="mb-0">
              <b-form-input
                v-model.number="std.breakMinutes"
                type="number"
                min="0"
                data-test="std-break"
              />
            </b-form-group>
          </div>
          <b-form-checkbox-group
            v-model="std.days"
            :options="stdDayOptions"
            class="mt-2"
            data-test="std-days"
          />
          <b-button
            variant="outline-primary"
            size="sm"
            class="mt-2"
            :disabled="!std.start || !std.finish || !std.days.length"
            data-test="std-apply"
            @click="applyStandardHours"
          >
            Apply to {{ std.days.length }}
            {{ std.days.length === 1 ? 'day' : 'days' }}
          </b-button>
        </section>

        <!-- Days -->
        <section class="wk-card">
          <div class="wk-total">
            <h2 class="wk-h2 mb-0">Your hours</h2>
            <span data-test="week-total">
              Total {{ formatHours(totalMinutes) || '0h' }}
            </span>
          </div>
          <div
            v-for="(day, i) in days"
            :key="day.date"
            class="wk-day"
            :class="{ 'wk-day--error': dayErrors[i] }"
            :data-test="`day-${day.date}`"
          >
            <div class="wk-day-head">
              <strong>{{ dayLabel(day.date) }}</strong>
              <span>{{
                day.leave_type
                  ? leaveTypeLabel(day.leave_type)
                  : formatHours(dayMinutes(day)) || '0h'
              }}</span>
            </div>
            <div class="wk-day-fields">
              <b-form-group label="Type" class="mb-0">
                <b-form-select
                  :value="day.leave_type"
                  :options="leaveOptions"
                  :disabled="!editable || !!day.leave_locked"
                  @change="setDay(i, 'leave_type', $event)"
                />
              </b-form-group>
              <template v-if="!day.leave_type">
                <b-form-group label="Start" class="mb-0">
                  <b-form-input
                    :value="day.start"
                    type="time"
                    :disabled="!editable"
                    :data-test="`start-${day.date}`"
                    @change="setDay(i, 'start', $event)"
                  />
                </b-form-group>
                <b-form-group label="Finish" class="mb-0">
                  <b-form-input
                    :value="day.finish"
                    type="time"
                    :disabled="!editable"
                    :data-test="`finish-${day.date}`"
                    @change="setDay(i, 'finish', $event)"
                  />
                </b-form-group>
                <b-form-group label="Break (min)" class="mb-0">
                  <b-form-input
                    :value="day.break_minutes"
                    type="number"
                    min="0"
                    :disabled="!editable"
                    @change="setDay(i, 'break_minutes', $event)"
                  />
                </b-form-group>
              </template>
            </div>
            <small v-if="dayErrors[i]" class="text-danger">
              {{ dayErrors[i] }}
            </small>
            <small v-else-if="day.leave_locked" class="text-muted">
              Leave set by your company admin.
            </small>
          </div>
        </section>

        <!-- Plant -->
        <section v-if="plantEnabled" class="wk-card" data-test="plant">
          <div class="wk-total">
            <h2 class="wk-h2 mb-0">Plant &amp; Equipment</h2>
            <b-button
              v-if="editable"
              variant="outline-primary"
              size="sm"
              data-test="add-plant"
              @click="openAddPlant"
            >
              Add equipment
            </b-button>
          </div>
          <p v-if="!plantEntries.length" class="text-muted mb-0 mt-2">
            No equipment added this week.
          </p>
          <div
            v-for="(pe, i) in plantEntries"
            :key="`${pe.date}-${pe.plant_equipment_id}-${i}`"
            class="wk-plant"
          >
            <span>
              {{ dayLabel(pe.date) }} · {{ pe.plant_name || 'Equipment' }}
              <span v-if="pe.plant_id_rego" class="text-muted"
                >({{ pe.plant_id_rego }})</span
              >
              · {{ pe.quantity }}
              {{ plantUnitLabel(pe.rate_type_snapshot) }}
            </span>
            <b-button
              v-if="editable"
              variant="link"
              size="sm"
              class="text-danger p-0"
              @click="removePlant(i)"
            >
              Remove
            </b-button>
          </div>
        </section>

        <!-- Photos -->
        <section class="wk-card" data-test="photos">
          <div class="wk-total">
            <h2 class="wk-h2 mb-0">Photos</h2>
            <label
              v-if="editable"
              class="btn btn-outline-primary btn-sm mb-0"
              :class="{ disabled: uploadingPhotos }"
            >
              {{ uploadingPhotos ? 'Uploading…' : 'Add photos' }}
              <input
                type="file"
                accept="image/*"
                multiple
                hidden
                :disabled="uploadingPhotos"
                data-test="photo-input"
                @change="onPhotosPicked"
              />
            </label>
          </div>
          <p v-if="!photos.length" class="text-muted mb-0 mt-2">
            No photos added.
          </p>
          <div v-else class="wk-photos">
            <div v-for="photo in photos" :key="photo.id" class="wk-photo">
              <a :href="photo.url" target="_blank" rel="noopener noreferrer">
                <img :src="photo.url" alt="Timesheet photo" />
              </a>
              <b-button
                v-if="editable"
                variant="link"
                size="sm"
                class="text-danger p-0"
                :disabled="deletingPhotoId === photo.id"
                @click="removePhoto(photo.id)"
              >
                Remove
              </b-button>
            </div>
          </div>
        </section>

        <!-- Comment -->
        <section class="wk-card">
          <h2 class="wk-h2">Comment</h2>
          <b-form-textarea
            v-model="comment"
            rows="3"
            maxlength="1000"
            :disabled="!editable"
            placeholder="Anything your supervisor should know about this week"
            data-test="comment"
          />
        </section>

        <!-- Actions -->
        <section v-if="editable" class="wk-actions">
          <b-button
            v-if="isDirty"
            variant="outline-secondary"
            :disabled="saving"
            data-test="discard"
            @click="discard"
          >
            Discard changes
          </b-button>
          <b-button
            variant="outline-primary"
            :disabled="saving || !isDirty"
            data-test="save"
            @click="saveDraft"
          >
            {{ saving ? 'Saving…' : 'Save draft' }}
          </b-button>
          <b-button
            variant="primary"
            :disabled="saving"
            data-test="review"
            @click="review"
          >
            Review &amp; send
          </b-button>
        </section>
      </template>
    </template>

    <weekly-send-modal
      :sheet="sheet"
      :project-name="projectName"
      @sent="onSent"
    />

    <add-crew-plant-modal
      modal-id="weekly-add-plant-modal"
      :week-dates="plantWeekDates"
      :equipment-options="equipmentOptions"
      @save="onPlantAdded"
    />
  </div>
</template>

<script>
import dayjs from 'dayjs'
import AddCrewPlantModal from '@/components/timesheets/supervisor/AddCrewPlantModal.vue'
import WeeklySendModal from '@/components/timesheets/weekly/WeeklySendModal.vue'
import {
  LEAVE_TYPE_OPTIONS,
  buildEquipmentOptions,
  dayMinutes,
  formatHours,
  leaveTypeLabel,
  plantUnitLabel,
  dayError,
} from '@/utils/timesheetHelpers'
import { addDays, settingsStartDay, startDayOf } from '@/utils/weekHelpers'
import {
  buildSaveBody,
  editCancelsLink,
  firstDayError,
  fillStandardHours,
  isEditableByOwner,
  ownDays,
  standardHoursImpact,
  weekTotalMinutes,
  weeklyStatusLabel,
  withDayField,
} from '@/utils/myWeekSheet'

const clone = (v) => JSON.parse(JSON.stringify(v))
const apiMessage = (e, fallback) =>
  (e && e.response && e.response.data && e.response.data.message) || fallback

// The tradie's own week: seven day cards, Standard Hours, plant, photos and a
// comment, saved as a draft. Mirrors the app's WeeklyEntryTab; sending to a
// supervisor is the review step.
export default {
  components: { AddCrewPlantModal, WeeklySendModal },

  props: {
    // Opens this project-week on load (amending from History & Status).
    initialProjectId: { type: Number, default: null },
    initialWeekStart: { type: String, default: null },
  },

  data() {
    return {
      projects: [],
      loadingProjects: true,
      projectsError: null,
      projectId: null,
      settings: null,
      weekStart: null,
      sheet: null,
      loadingSheet: false,
      sheetError: null,
      days: [],
      comment: '',
      plantEntries: [],
      plantDirty: false,
      saving: false,
      std: { start: '', finish: '', breakMinutes: 30, days: [] },
      equipmentOptions: [{ value: null, text: 'Select equipment' }],
      uploadingPhotos: false,
      deletingPhotoId: null,
    }
  },

  computed: {
    projectOptions() {
      return [
        { value: null, text: 'Select a project', disabled: true },
        ...this.projects.map((p) => ({
          value: p.id,
          text: p.company_name ? `${p.name} (${p.company_name})` : p.name,
        })),
      ]
    },
    projectName() {
      const p = this.projects.find((x) => x.id === this.projectId)
      return p ? p.name : ''
    },
    // Worked first, then the leave types; leave hours are the admin's to set.
    leaveOptions() {
      return LEAVE_TYPE_OPTIONS
    },
    startDay() {
      return settingsStartDay(this.settings)
    },
    plantEnabled() {
      return (
        !this.settings ||
        this.settings.supervisor_crew_equipment_enabled !== false
      )
    },
    weekLabel() {
      if (!this.weekStart) return ''
      const end = dayjs(this.weekStart).add(6, 'day')
      return `${dayjs(this.weekStart).format('ddd D MMM')} – ${end.format(
        'ddd D MMM YYYY',
      )}`
    },
    editable() {
      return isEditableByOwner(this.sheet)
    },
    cancelsLink() {
      return editCancelsLink(this.sheet)
    },
    statusLabel() {
      return weeklyStatusLabel(this.sheet)
    },
    rejectionNote() {
      const r = this.sheet && this.sheet.external_request
      return (r && r.comment) || (this.sheet && this.sheet.rejection_reason)
    },
    totalMinutes() {
      return weekTotalMinutes(this.days)
    },
    dayErrors() {
      return this.days.map((d) => (d.leave_type ? null : dayError(d)))
    },
    stdDayOptions() {
      return this.days.map((d, i) => ({
        value: i,
        text: dayjs(d.date).format('ddd'),
        disabled: !!d.leave_locked,
      }))
    },
    photos() {
      return (this.sheet && this.sheet.photo_urls) || []
    },
    workerId() {
      const row = this.sheet && this.sheet.rows && this.sheet.rows[0]
      return row && row.worker && row.worker.id
    },
    saveBody() {
      return buildSaveBody({
        workerId: this.workerId,
        days: this.days,
        comment: this.comment,
        plantEntries: this.plantDirty ? this.plantEntries : undefined,
      })
    },
    baselineBody() {
      return this.sheet
        ? buildSaveBody({
            workerId: this.workerId,
            days: ownDays(this.sheet),
            comment: this.sheet.supervisor_comment || '',
          })
        : null
    },
    isDirty() {
      if (!this.sheet) return false
      if (this.plantDirty) return true
      return JSON.stringify(this.saveBody) !== JSON.stringify(this.baselineBody)
    },
    plantWeekDates() {
      return this.days.map((d) => ({
        iso: d.date,
        label: dayjs(d.date).format('ddd').toUpperCase(),
        dayNum: dayjs(d.date).date(),
      }))
    },
  },

  created() {
    this.loadProjects()
    window.addEventListener('beforeunload', this.onBeforeUnload)
  },

  beforeDestroy() {
    window.removeEventListener('beforeunload', this.onBeforeUnload)
  },

  methods: {
    formatHours,
    dayMinutes,
    leaveTypeLabel,
    plantUnitLabel,

    dayLabel(date) {
      return dayjs(date).format('ddd D MMM')
    },

    onBeforeUnload(e) {
      if (!this.isDirty) return
      e.preventDefault()
      e.returnValue = ''
    },

    async loadProjects() {
      this.loadingProjects = true
      this.projectsError = null
      try {
        this.projects = await this.$store.dispatch('timesheet/myWeekProjects')
      } catch (e) {
        this.projectsError = apiMessage(e, 'Could not load your projects.')
      } finally {
        this.loadingProjects = false
      }
      // One project is the common case: open its current week straight away.
      const wanted = this.projects.find((p) => p.id === this.initialProjectId)
      if (wanted) {
        await this.selectProject(wanted.id, this.initialWeekStart)
      } else if (this.projects.length === 1) {
        await this.selectProject(this.projects[0].id)
      }
    },

    async onProjectChange(id) {
      if (!(await this.confirmLeaving())) {
        // Put the select back on the project still open.
        const keep = this.projectId
        this.projectId = null
        this.$nextTick(() => (this.projectId = keep))
        return
      }
      await this.selectProject(id)
    },

    async selectProject(id, weekStart = null) {
      this.projectId = id
      this.settings = await this.$store.dispatch('timesheet/myWeekSettings', id)
      this.weekStart = startDayOf(
        weekStart || this.weekStart || dayjs().format('YYYY-MM-DD'),
        this.startDay,
      )
      this.loadEquipment(id)
      await this.openWeek()
    },

    async loadEquipment(projectId) {
      try {
        const items = await this.$store.dispatch(
          'plantEquipment/getMyPlantEquipment',
          { project_id: projectId },
        )
        this.equipmentOptions = buildEquipmentOptions(items)
      } catch (e) {
        this.equipmentOptions = [{ value: null, text: 'Select equipment' }]
      }
    },

    async openWeek() {
      if (!this.projectId || !this.weekStart) return
      const projectId = this.projectId
      this.loadingSheet = true
      this.sheetError = null
      try {
        const sheet = await this.$store.dispatch('timesheet/myWeekOpen', {
          projectId,
          weekStart: this.weekStart,
        })
        if (projectId !== this.projectId) return
        if (!sheet || !sheet.rows || !sheet.rows.length) {
          this.sheetError = 'Could not load your timesheet.'
          return
        }
        this.applyServerSheet(sheet)
      } catch (e) {
        this.sheet = null
        this.sheetError = apiMessage(e, 'Could not load your timesheet.')
      } finally {
        this.loadingSheet = false
      }
    },

    // A sheet the server returned is both the working copy and the baseline.
    applyServerSheet(sheet) {
      this.sheet = sheet
      // The server snaps the week to the company's week start.
      this.weekStart = sheet.week_start
      this.days = clone(ownDays(sheet))
      this.comment = sheet.supervisor_comment || ''
      this.plantEntries = clone(sheet.plant_entries || [])
      this.plantDirty = false
      this.std.days = this.days
        .map((d, i) => (d.leave_locked ? null : i))
        .filter((i) => i !== null)
    },

    async shiftWeek(n) {
      await this.changeWeek(addDays(this.weekStart, n * 7))
    },

    async onWeekPicked(value) {
      if (!value) return
      await this.changeWeek(startDayOf(value, this.startDay))
    },

    async changeWeek(weekStart) {
      if (weekStart === this.weekStart) return
      const previous = this.weekStart
      if (!(await this.confirmLeaving())) {
        this.weekStart = null
        this.$nextTick(() => (this.weekStart = previous))
        return
      }
      this.weekStart = weekStart
      await this.openWeek()
    },

    // Before switching project or week: saves unsaved edits. A pending sheet
    // is never saved silently, since that cancels its link; the tradie picks.
    async confirmLeaving() {
      if (!this.sheet || !this.editable || !this.isDirty) return true
      if (this.cancelsLink) {
        const save = await this.$bvModal.msgBoxConfirm(
          'This week is awaiting approval. Saving takes it back to draft and cancels the link sent to your supervisor.',
          {
            title: 'Save your changes?',
            okTitle: 'Save',
            cancelTitle: 'Discard',
            centered: true,
            modalClass: 'confirm-modal',
          },
        )
        if (save === null) return false
        if (!save) return true
      }
      return this.save({ silent: true })
    },

    // Asks before a write that would cancel a pending sheet's link.
    async confirmCancelsLink() {
      if (!this.cancelsLink) return true
      const ok = await this.$bvModal.msgBoxConfirm(
        'This week is awaiting approval. Changing it takes it back to draft and cancels the link sent to your supervisor. You can send it again after.',
        {
          title: 'Cancel the approval link?',
          okTitle: 'Continue',
          okVariant: 'warning',
          centered: true,
          modalClass: 'confirm-modal',
        },
      )
      return ok === true
    },

    setDay(i, field, value) {
      this.days.splice(i, 1, withDayField(this.days[i], field, value))
    },

    async applyStandardHours() {
      const probe = withDayField(
        withDayField({ present: true }, 'start', this.std.start),
        'finish',
        this.std.finish,
      )
      probe.break_minutes = Number(this.std.breakMinutes) || 0
      const invalid = dayError(probe)
      if (invalid) {
        this.toast(invalid, 'danger')
        return
      }
      const impact = standardHoursImpact(this.days, this.std.days)
      if (impact.conflicts > 0) {
        const n = impact.conflicts
        const ok = await this.$bvModal.msgBoxConfirm(
          `${n} ${
            n === 1 ? 'day already has' : 'days already have'
          } hours. Applying Standard Hours will overwrite ${
            n === 1 ? 'it' : 'them'
          }.`,
          {
            title: 'Overwrite existing hours?',
            okTitle: 'Overwrite',
            okVariant: 'danger',
            centered: true,
            modalClass: 'confirm-modal',
          },
        )
        if (!ok) return
      }
      this.days = fillStandardHours(
        this.days,
        {
          start: this.std.start,
          finish: this.std.finish,
          breakMinutes: this.std.breakMinutes,
        },
        this.std.days,
      )
      if (impact.skippedLeave > 0) {
        const n = impact.skippedLeave
        this.toast(`Skipped ${n} ${n === 1 ? 'day' : 'days'} on leave.`)
      }
    },

    discard() {
      if (this.sheet) this.applyServerSheet(this.sheet)
    },

    async saveDraft() {
      if (!(await this.confirmCancelsLink())) return
      await this.save()
    },

    // PUTs the working week and takes the server's answer as the new
    // baseline (a pending sheet comes back as draft).
    async save({ silent = false } = {}) {
      const invalid = firstDayError(this.days)
      if (invalid) {
        this.toast(invalid, 'danger')
        return false
      }
      this.saving = true
      try {
        const sheet = await this.$store.dispatch('timesheet/myWeekSave', {
          id: this.sheet.id,
          body: this.saveBody,
        })
        if (sheet && sheet.rows) this.applyServerSheet(sheet)
        if (!silent) this.toast('Draft saved.', 'success')
        return true
      } catch (e) {
        this.toast(apiMessage(e, 'Could not save your draft.'), 'danger')
        return false
      } finally {
        this.saving = false
      }
    },

    // Saves unsaved edits first (asking if that cancels a pending link), then
    // opens the send step on the saved week.
    async review() {
      if (this.isDirty) {
        if (!(await this.confirmCancelsLink())) return
        if (!(await this.save({ silent: true }))) return
      }
      this.$bvModal.show('weekly-send-modal')
    },

    onSent({ sheet, supervisor }) {
      if (sheet && sheet.rows) this.applyServerSheet(sheet)
      this.toast(`Sent to ${supervisor.name} for approval.`, 'success')
      this.$emit('sent')
    },

    openAddPlant() {
      this.$bvModal.show('weekly-add-plant-modal')
    },

    onPlantAdded({ date, entries }) {
      if (!entries.length) return
      const items = this.$store.getters['plantEquipment/items'] || []
      const byId = new Map(items.map((item) => [item.id, item]))
      entries.forEach((e) => {
        const item = byId.get(e.plant_equipment_id)
        this.plantEntries.push({
          date,
          plant_equipment_id: e.plant_equipment_id,
          plant_name: item ? item.name : '',
          plant_id_rego: item ? item.plant_id_rego : null,
          quantity: e.quantity,
          rate_type_snapshot: item ? item.rate_type : null,
        })
      })
      this.plantDirty = true
    },

    removePlant(i) {
      this.plantEntries.splice(i, 1)
      this.plantDirty = true
    },

    // Uploaded on pick, like the crew sheet. On a pending sheet an upload
    // also returns it to draft server-side, so ask first.
    async onPhotosPicked(e) {
      const files = Array.from(e.target.files || []).filter(
        (f) => f.type !== 'image/gif',
      )
      e.target.value = ''
      if (!files.length) return
      if (!(await this.confirmCancelsLink())) return
      this.uploadingPhotos = true
      try {
        const urls = await this.$store.dispatch(
          'timesheet/myWeekUploadPhotos',
          { id: this.sheet.id, files },
        )
        await this.refreshAfterPhotos(urls)
        this.toast(`${files.length} photo(s) uploaded.`, 'success')
      } catch (err) {
        this.toast(apiMessage(err, 'Could not upload your photos.'), 'danger')
      } finally {
        this.uploadingPhotos = false
      }
    },

    async removePhoto(mediaId) {
      const ok = await this.$bvModal.msgBoxConfirm('Remove this photo?', {
        title: 'Remove photo?',
        okTitle: 'Remove',
        okVariant: 'danger',
        centered: true,
        modalClass: 'confirm-modal',
      })
      if (!ok || !(await this.confirmCancelsLink())) return
      this.deletingPhotoId = mediaId
      try {
        const urls = await this.$store.dispatch('timesheet/myWeekDeletePhoto', {
          id: this.sheet.id,
          mediaId,
        })
        await this.refreshAfterPhotos(urls)
      } catch (err) {
        this.toast(apiMessage(err, 'Could not remove the photo.'), 'danger')
      } finally {
        this.deletingPhotoId = null
      }
    },

    // Photos write straight away; a pending sheet is now a draft, so reload
    // its status, keeping any unsaved day edits.
    async refreshAfterPhotos(urls) {
      if (this.cancelsLink) {
        const fresh = await this.$store.dispatch(
          'timesheet/myWeekFetch',
          this.sheet.id,
        )
        if (fresh) {
          this.sheet = { ...fresh, photo_urls: urls || fresh.photo_urls }
          return
        }
      }
      this.sheet = { ...this.sheet, photo_urls: urls || [] }
    },

    // On the root, so a toast raised as the page swaps this form out (after a
    // send) still shows.
    toast(message, variant = 'info') {
      this.$root.$bvToast.toast(message, {
        variant,
        solid: true,
        autoHideDelay: 4000,
        title:
          variant === 'danger'
            ? 'Error'
            : variant === 'success'
            ? 'Done'
            : 'Weekly timesheet',
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.wk-card {
  background: #fff;
  border: 1px solid #e4e7eb;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 16px;
}
.wk-h2 {
  font-size: 1.05rem;
  font-weight: 700;
  margin-bottom: 8px;
}
.wk-pickers {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 12px;
}
.wk-week {
  display: flex;
  gap: 6px;
}
.wk-std {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
}
.wk-total {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 8px;
}
.wk-day {
  border: 1px solid #e4e7eb;
  border-radius: 10px;
  padding: 12px;
  margin-top: 10px;
}
.wk-day--error {
  border-color: #dc3545;
}
.wk-day-head {
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
}
.wk-day-fields {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(130px, 1fr));
  gap: 8px;
}
.wk-plant {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  padding: 6px 0;
  border-top: 1px solid #f0f2f4;
}
.wk-photos {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 8px;
}
.wk-photo img {
  width: 110px;
  height: 110px;
  object-fit: cover;
  border-radius: 8px;
  display: block;
}
.wk-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 8px;
  margin-bottom: 24px;
}
@media (max-width: 575px) {
  .wk-std {
    grid-template-columns: 1fr;
  }
}
</style>
